import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Modal } from '@els/els-react--modal';
import { resultsState } from '@Pages/Results/selectors';
import { EXPORT_VIEWS } from './ExportModalConstants';
import ExportModalHeader from './Components/DefaultExportModal/ExportModalHeader';
import ExportModalContent from './Components/DefaultExportModal/ExportModalContent';
import ExportModalFooter from './Components/DefaultExportModal/ExportModalFooter';
import ChemquoteExportModalHeader from './Components/ChemquoteExportModal/ChemquoteExportModalHeader';
import ChemquoteExportModalContent from './Components/ChemquoteExportModal/ChemquoteExportModalContent';
import ChemquoteExportModalFooter from './Components/ChemquoteExportModal/ChemquoteExportModalFooter';
import DatapointsExportModalHeader from './Components/DatapointsExportModal/DatapointsExportModalHeader';
import DatapointsExportModalContent from './Components/DatapointsExportModal/DatapointsExportModalContent';
import DatapointsExportModalFooter from './Components/DatapointsExportModal/DatapointsExportModalFooter';

import './ExportModal.scss';
import { setData, setEntries } from './ExportModalActions';

function ExportModal(props) {
  const { initialize, close, isChemQuoteExport, currentView, data, formData, entries } = props;
  const dispatch = useDispatch();
  const resultsPageState = useSelector((state) => resultsState(state));
  const history = useHistory();

  const checkedResults = get(resultsPageState, 'checkedItems.results', []);

  useEffect(() => {
    initialize(history);

    return () => {
      close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatedData = useMemo(() => {
    const cloneData = cloneDeep(data);
    if (checkedResults.length > 0 && Array.isArray(data?.ranges) && data?.ranges.length > 1 && data?.ranges[1]?.value === 0) {
      cloneData.ranges[1].value = checkedResults.length;
      dispatch(setData(cloneData));
    }
    return cloneData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, checkedResults]);

  useEffect(() => {
    const cloneEntries = cloneDeep(entries);
    if (checkedResults.length > 0 && entries?.selected?.length <= 0) {
      cloneEntries.selected = [...checkedResults];
      dispatch(setEntries(cloneDeep(cloneEntries)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, checkedResults]);

  // Return focus to the "Add datapoints" trigger when the datapoints sub-view closes (WCAG 2.4.3).
  const previousViewRef = useRef(currentView);
  useEffect(() => {
    const returnedToMain = previousViewRef.current === EXPORT_VIEWS.DATAPOINTS && currentView === EXPORT_VIEWS.MAIN;
    previousViewRef.current = currentView;
    if (!returnedToMain) {
      return undefined;
    }
    const frameId = requestAnimationFrame(() => {
      document.querySelector('.e2e-export-modal-content-add-datapoints')?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, [currentView]);

  if (!data || !formData) {
    return null;
  }

  const resolveComponents = () => {
    if (isChemQuoteExport) {
      return {
        Header: ChemquoteExportModalHeader,
        Content: ChemquoteExportModalContent,
        Footer: ChemquoteExportModalFooter,
      };
    }
    if (currentView === EXPORT_VIEWS.DATAPOINTS) {
      return {
        Header: DatapointsExportModalHeader,
        Content: DatapointsExportModalContent,
        Footer: DatapointsExportModalFooter,
      };
    }
    return {
      Header: ExportModalHeader,
      Content: ExportModalContent,
      Footer: ExportModalFooter,
    };
  };

  const { Header, Content, Footer } = resolveComponents();
  const isDatapointsView = !isChemQuoteExport && currentView === EXPORT_VIEWS.DATAPOINTS;

  return (
    <Modal
      onClose={close}
      shouldCloseOnEscape
      shouldPreventClickOverlayToClose
      shouldHideCloseButton
      className={classnames('react-dialogs-scrollable exportModalModal', { datapointsExportModal: isDatapointsView })}
      id={isDatapointsView ? 'datapoints-export-modal' : 'export-modal'}
      label={isDatapointsView ? 'Export datapoints' : 'Export'}>
      <div className={isDatapointsView ? 'datapointsExportModal__content' : 'exportModal__dialog'}>
        <Header {...props} data={updatedData} formData={formData} />
        <Content {...props} data={updatedData} formData={formData} />
        <Footer {...props} data={updatedData} formData={formData} />
      </div>
    </Modal>
  );
}

ExportModal.propTypes = {
  data: PropTypes.object,
  formData: PropTypes.object,
  initialize: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  isChemQuoteExport: PropTypes.bool,
  currentView: PropTypes.oneOf([EXPORT_VIEWS.MAIN, EXPORT_VIEWS.DATAPOINTS]),
};

export default ExportModal;
